import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dayjs } from 'dayjs';
import { DateRange } from '@mui/x-date-pickers-pro';
import { TableColumnInterface } from '../../interfaces/tableColumn.interface';
import BasicTable from './basicTable';
import { FORM_SUBMISSIONS } from '../../constants/pageIdentifiers';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants/constants';
import { userFormInputs } from '../../constants/modalInputs/userFormInputs';
import DateCell from '../../partials/dateCell';
import useGetFormSubmissions from '../../apiHooks/queries/useGetFormSubmissions';
import {
  useGetExportFormSubmissionsTable,
} from '../../apiHooks/mutations/reportsExportMutations/useGetFormSubmissionsTable';

const FormSubmissionTable = () => {
  const baseColumns: TableColumnInterface[] = [
    { field: 'adCode', headerName: 'adCode' },
    { field: 'sessionId', headerName: 'sessionId' },
    { field: 'path', headerName: 'path' },
    { field: 'referrer', headerName: 'referrer' },
    { field: 'browser', headerName: 'browser' },
    { field: 'status', headerName: 'status' },
    { field: 'superOfficeId', headerName: 'superOfficeId' },
    { field: 'id', headerName: 'id' },
    { field: 'income', headerName: 'income' },
    { field: 'incomeAt', headerName: 'incomeAt' },
    { field: 'organizationId', headerName: 'organizationId' },
    { field: 'originalReferrer', headerName: 'originalReferrer' },
    {
      field: 'createdAt',
      headerName: 'createdAt',
      cellRenderer: (params: string) => <DateCell params={params} />,
    },
  ];

  const sortablesList = ['adCode', 'sessionId', 'path', 'createdAt'];

  const [allColumns, setAllColumns] = useState<TableColumnInterface[]>(baseColumns);
  const [columns, setColumns] = useState<TableColumnInterface[]>(baseColumns);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [usersFilterDateRange, setUsersFilterDateRange] = useState<DateRange<Dayjs>>([null, null]);
  const [sortBy, setSortBy] = useState<string>('');
  const [orderBy, setOrderBy] = useState<string>('');

  const [selectedColumns, setSelectedColumns] = useState<{ [key: string]: boolean }>({});
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const canPinRows = (_id: number) => true;

  const [searchInput, setSearchInput] = useState({
    adCode: '',
    status: null as number | null,
    referrer: '',
    superOfficeId: '',
    originalReferrer: '',
  });

  const {
    data: usersData, isError, refetch: refetchUsersData, isFetching,
  } = useGetFormSubmissions(
    FORM_SUBMISSIONS,
    searchInput.adCode,
    searchInput.status,
    searchInput.referrer,
    searchInput.originalReferrer,
    searchInput.superOfficeId,
    orderBy,
    sortBy,
    usersFilterDateRange[0],
    usersFilterDateRange[1],
    rowsPerPage,
    page,
    selectedColumns || null,
  );

  const { mutate: fetchDownload } = useGetExportFormSubmissionsTable({
    adCode: searchInput.adCode,
    status: searchInput.status,
    orderBy,
    sortBy,
    from: usersFilterDateRange[0],
    to: usersFilterDateRange[1],
    rows: rowsPerPage,
    page: page + 1,
    fields: selectedColumns || null,
  });

  useEffect(() => {
    if (usersData && usersData.data?.report && usersData.data.report.length > 0 && isInitialLoad) {
      const keys = Object.keys(usersData.data.report[0]);
      const initialSelectedColumns = keys.reduce((acc, key) => {
        acc[key] = false;
        return acc;
      }, {} as { [key: string]: boolean });

      const dynamicNewCols = keys
        .filter((key) => !allColumns.find((col) => col.field === key))
        .map((key) => ({ field: key, headerName: key }));

      if (dynamicNewCols.length > 0) {
        setAllColumns((prev) => [...prev, ...dynamicNewCols]);
      }

      setSelectedColumns(initialSelectedColumns);
      setIsInitialLoad(false);
    }
  }, [allColumns, isInitialLoad, usersData]);

  useEffect(() => {
    if (!isInitialLoad) refetchUsersData();
  }, [searchInput, selectedColumns]);

  useEffect(() => {
    if (!isInitialLoad) {
      const selectedFields = Object.keys(selectedColumns).filter((key) => selectedColumns[key]);
      const newColumns = selectedFields.length > 0
        ? allColumns.filter((col) => selectedFields.includes(col.field))
        : allColumns;

      setColumns(newColumns);
      refetchUsersData();
    }
  }, [allColumns, isInitialLoad, refetchUsersData, selectedColumns]);

  return (
    <BasicTable
      data={usersData?.data ?? []}
      fetchDownload={fetchDownload}
      fsTable
      refetch={refetchUsersData}
      canPinRows={canPinRows}
      formInputs={userFormInputs}
      setPage={(newPage) => setPage(newPage)}
      setRowsPerPage={(newRowsPerPage) => setRowsPerPage(newRowsPerPage)}
      setSearchInput={setSearchInput}
      setSortBy={setSortBy}
      setOrderBy={setOrderBy}
      hasNextPage={usersData && usersData?.data.hasNextPage}
      filterDateRange={usersFilterDateRange}
      setFilterDateRange={setUsersFilterDateRange}
      {...{
        isError,
        isFetching,
        page,
        searchInput,
        columns,
        rowsPerPage,
        orderBy,
        sortBy,
        sortablesList,
      }}
    />
  );
};

export default FormSubmissionTable;
